import { defineStore } from 'pinia'
import { ref } from 'vue'

export interface DashboardWidget {
  id: string
  type: string
  x: number
  y: number
  width: number
  height: number
  config?: Record<string, unknown>
}

export interface Dashboard {
  id: number
  slug: string
  name: string
  widgets: DashboardWidget[]
  is_default: boolean
}

// Debounce pour l'auto-save
let saveTimeout: ReturnType<typeof setTimeout> | null = null
const SAVE_DELAY = 1000 // 1 seconde après la dernière modification

export const useDashboardStore = defineStore('dashboard', () => {
  const isEditMode = ref(false)
  const showWidgetSelector = ref(false)
  const widgets = ref<DashboardWidget[]>([])
  const currentSlug = ref('main')
  const currentDashboard = ref<Dashboard | null>(null)
  const isLoading = ref(false)
  const isSaving = ref(false)
  const hasUnsavedChanges = ref(false)
  const lastSaveError = ref<string | null>(null)

  /**
   * Charge le dashboard depuis l'API
   */
  async function loadDashboard(slug: string = 'main') {
    isLoading.value = true
    lastSaveError.value = null

    try {
      const response = await fetch(`/api/dashboards/${slug}`, {
        credentials: 'include',
      })

      if (!response.ok) {
        throw new Error('Erreur lors du chargement du dashboard')
      }

      const data = await response.json()
      if (data.success && data.data.dashboard) {
        currentDashboard.value = data.data.dashboard
        currentSlug.value = slug
        widgets.value = data.data.dashboard.widgets || []
        hasUnsavedChanges.value = false
      }
    } catch (error) {
      console.error('Erreur chargement dashboard:', error)
      lastSaveError.value = 'Erreur lors du chargement'
    } finally {
      isLoading.value = false
    }
  }

  /**
   * Sauvegarde le dashboard vers l'API
   */
  async function saveDashboard() {
    if (!currentSlug.value) return

    isSaving.value = true
    lastSaveError.value = null

    try {
      const response = await fetch(`/api/dashboards/${currentSlug.value}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          widgets: widgets.value,
        }),
      })

      if (!response.ok) {
        throw new Error('Erreur lors de la sauvegarde')
      }

      const data = await response.json()
      if (data.success) {
        hasUnsavedChanges.value = false
        if (data.data.dashboard) {
          currentDashboard.value = data.data.dashboard
        }
      } else {
        throw new Error(data.message || 'Erreur inconnue')
      }
    } catch (error) {
      console.error('Erreur sauvegarde dashboard:', error)
      lastSaveError.value = error instanceof Error ? error.message : 'Erreur de sauvegarde'
    } finally {
      isSaving.value = false
    }
  }

  /**
   * Programme une sauvegarde avec debounce
   */
  function scheduleSave() {
    hasUnsavedChanges.value = true

    if (saveTimeout) {
      clearTimeout(saveTimeout)
    }

    saveTimeout = setTimeout(() => {
      saveDashboard()
    }, SAVE_DELAY)
  }

  function toggleEditMode() {
    isEditMode.value = !isEditMode.value

    // Sauvegarder quand on quitte le mode édition
    if (!isEditMode.value && hasUnsavedChanges.value) {
      saveDashboard()
    }
  }

  function openWidgetSelector() {
    showWidgetSelector.value = true
  }

  function closeWidgetSelector() {
    showWidgetSelector.value = false
  }

  function addWidget(widget: DashboardWidget) {
    widgets.value.push(widget)
    scheduleSave()
  }

  function removeWidget(id: string) {
    const index = widgets.value.findIndex(w => w.id === id)
    if (index !== -1) {
      widgets.value.splice(index, 1)
      scheduleSave()
    }
  }

  function updateWidget(widget: DashboardWidget) {
    const index = widgets.value.findIndex(w => w.id === widget.id)
    if (index !== -1) {
      widgets.value[index] = widget
      scheduleSave()
    }
  }

  return {
    // State
    isEditMode,
    showWidgetSelector,
    widgets,
    currentSlug,
    currentDashboard,
    isLoading,
    isSaving,
    hasUnsavedChanges,
    lastSaveError,

    // Actions
    loadDashboard,
    saveDashboard,
    toggleEditMode,
    openWidgetSelector,
    closeWidgetSelector,
    addWidget,
    removeWidget,
    updateWidget,
  }
})
